// multiCcsFetches.js
// -----------------------------------------------------------------------------
// Centralized API layer for MultiCCS. Every network call the sub-app makes lives
// here and is handed down from MultiCcsApp as a single `api` prop.
//
// Two groups of functions:
//
//   1. FORKED FROM App.js — startCase / incompleteStart / grading. These are
//      deliberate copies of the battle-tested logic in App.js (all the date
//      munging, the streaming reader, the ialist handling) so that App.js does
//      not have to change. The difference: App's versions call this.setState()
//      and flip applicationView; these RESOLVE WITH DATA and let MultiCcsApp
//      decide what to do. Behaviour against the backend is identical.
//
//      If a bug is ever fixed in App.js's copies, it must be mirrored here.
//
//   2. NEW — the MULTICASE session endpoints.
//
// Every function takes an optional AbortSignal as its last argument.
// -----------------------------------------------------------------------------

import getDaysFromDate from "../Functions/GetDaysFromDate";
import formatCompletedOrders from "../Functions/IncompleteStart/formatCompletedOrders";
import formatCurrentOrders from "../Functions/IncompleteStart/formatCurrentOrders";
import getActionLogData from "../Functions/IncompleteStart/getActionLogData";
import logFetchError from "../Functions/LogFetchError";
import fetchSubmitLogs from "../Functions/FetchSubmitLogs";
import { fetchCaseInfo } from "../CaseInfoCache";

// Session sizing — 13 is the number of cases on a real Step 3 CCS exam.
export const MIN_BLOCK_SIZE = 2;
export const MAX_BLOCK_SIZE = 13;

// TIMEMODEID -> real-time minutes for the case. Mirrors TIMEMODEDESC
// ("10 Minute Case" / "20 Minute Case") returned by /listcasesinfo.webapi.
export const TIME_MODE_MINUTES = {
  1: 10,
  2: 20,
};

// Case statuses used inside CaseProgress.Cases[].Status
export const CASE_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
};

// -----------------------------------------------------------------------------
// AuthError — thrown on a 401 so MultiCcsApp can surface the app's standard
// unauthenticated popup instead of showing a generic failure.
// -----------------------------------------------------------------------------

export function AuthError() {
  const err = new Error("Authentication Error");
  err.isAuthError = true;
  return err;
}

function isAbort(error) {
  return error && error.name === "AbortError";
}

// -----------------------------------------------------------------------------
// createMultiCcsApi
//
//   route         string, e.g. "https://app.ccscases.com"       (required)
//   userData      the login payload — needs Token + CustomerId   (required)
//   userProfile   used only for log submission                   (optional)
//   onAuthError   () => void, called on any 401                  (optional)
// -----------------------------------------------------------------------------

export default function createMultiCcsApi(options) {
  const opts = options || {};
  const route = opts.route;
  const getUserData = function () {
    return typeof opts.userData === "function"
      ? opts.userData()
      : opts.userData;
  };
  const getUserProfile = function () {
    return typeof opts.userProfile === "function"
      ? opts.userProfile()
      : opts.userProfile;
  };
  const onAuthError = opts.onAuthError;

  function token() {
    const userData = getUserData();
    return userData ? userData.Token : "";
  }

  function customerId() {
    const userData = getUserData();
    return userData ? userData.CustomerId : null;
  }

  // Shared response handling: 401 -> notify host + throw; otherwise text.
  function handle(response) {
    console.log("Handling", response);
    if (response.status === 401) {
        console.log("auth error!",onAuthError)
      if (onAuthError) onAuthError();
      throw AuthError();
    }
    return response.text();
  }

  // Most of the legacy handlers send "text/plain" with a JSON body. Keep that
  // exactly — some of them are picky about the content type.
  function post(path, body, signal) {
    return fetch(route + path, {
      method: "POST",
      headers: {
        Token: token(),
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(body),
      signal: signal,
    }).then(handle);
  }

  function get(path, signal) {
    return fetch(route + path, {
      method: "GET",
      headers: { Token: token() },
      signal: signal,
    }).then(handle);
  }

  function logError(error, label) {
    if (isAbort(error) || (error && error.isAuthError)) return;
    console.log("error", error);
    logFetchError(error, getUserProfile(), getUserData(), label);
  }

  function submitLogs() {
    try {
      fetchSubmitLogs(getUserProfile(), getUserData());
    } catch (e) {
      /* logging is best-effort */
    }
  }

  function parseJson(text) {
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      return text;
    }
  }

  // ---------------------------------------------------------------------------
  // CASE LIST
  //
  // Same as App.fetchCaseList: /listcasesupdate.webapi returns per-customer rows
  // (COMPLETED, AvgGradeForCase, ReviewLaterFlag, LastCompletionDate,
  // LastCaseAttemptGrade, HIGHYIELDCOUNT) which get merged BY ARRAY INDEX with
  // the static case metadata (TITLE, DIAGNOSIS, CASECATEGORYDESC, TIMEMODEID,
  // TAGS...). Index-merging matches App.js — do not "improve" it to merge by ID
  // without changing App.js too, or the two lists would disagree.
  // ---------------------------------------------------------------------------

  async function listCases(signal) {
    let staticCasesInfo;
    try {
      // Memoized in CaseInfoCache — this only hits the network once per
      // login (shared with App.js's own fetch of the same endpoint), not
      // once per listCases() call.
      staticCasesInfo = await fetchCaseInfo(route, token());
    } catch (error) {
      logError(error, "multiCcs.listCases.staticFetch");
      throw error;
    }

    return get("/listcasesupdate.webapi", signal)
      .then(function (text) {
        submitLogs();
        const results = JSON.parse(text);
        const caseList = [];
        for (let i = 0; i < results.length; i++) {
          caseList.push(
            Object.assign({}, results[i], staticCasesInfo.cases[i]),
          );
        }
        return caseList;
      })
      .catch(function (error) {
        logError(error, "multiCcs.listCases");
        throw error;
      });
  }

  // ---------------------------------------------------------------------------
  // START CASE  (forked from App.fetchStartCase)
  //
  // Resolves with:
  //   { simulationData, startCaseData, caseguid, ialist }
  //
  // ialist is the list of the customer's incomplete attempts for this case, as
  // returned by the backend. App.js shows a popup to choose one; MultiCCS uses
  // it only when resuming, and matches on the CurrentStateId it stored itself.
  //
  // Throws an Error with .isCaseListError when the backend returns "Error: ..."
  // in place of a payload.
  // ---------------------------------------------------------------------------

  function startCase(caseData, signal) {
    // The initial simulation state for a fresh (non-resumed) case. Field-for-
    // field identical to App.fetchStartCase's local startCaseData.
    const startCaseData = {
      completedOrders: [],
      currentLocation: 2,
      currentOrders: [],
      currentPatientUpdate: "",
      currentPhysicalExamId: 10,
      currentState: "B0",
      currentVirtualTime: "",
      currentVitalsId: 9,
      timeToPatientUpdate: 0,
      popupQueue: ["introduction", "vitals", "history"],
      simulationOptionSelected: -1,
      centerContent: false,
      elapsedSimulatedCaseTime: { days: 0, hours: 0, minutes: 0 },
      physicalResults: [],
      currentActualTime: 0,
      caseEnds: false,
      patientUpdates: [],
      vitalsResults: [],
    };

    return post(
      "/startcase.webapi",
      { customerId: customerId(), caseId: caseData.ID },
      signal,
    )
      .then(function (result) {
        submitLogs();

        if (result.includes("Error: ")) {
          const err = new Error(result);
          err.isCaseListError = true;
          throw err;
        }

        const simulationData = JSON.parse(result);

        const date = new Date(simulationData.InitialStartTime + "Z");
        simulationData.InitialStartTime = {
          days: getDaysFromDate(date),
          hours: date.getUTCHours(),
          minutes: date.getUTCMinutes(),
        };

        startCaseData.elapsedSimulatedCaseTime = {
          days: simulationData.InitialStartTime.days,
          hours: simulationData.InitialStartTime.hours,
          minutes: simulationData.InitialStartTime.minutes,
        };

        // Cases that begin with the patient already on medication come back
        // with pre-populated orders that need their times reshaped.
        if (simulationData.StartMed) {
          const startingOrders = JSON.parse(simulationData.CurrentOrders);
          for (let i = 0; i < startingOrders.length; ++i) {
            const reportTime = new Date(startingOrders[i].ReportTime + "Z");
            const timeOrdered = new Date("" + startingOrders[i].TimeOrdered);
            const currentVirtualTime = new Date(
              startingOrders[i].CurrentVirtualTime + "Z",
            );

            if (startingOrders[i].ReportTime === "0001-01-01T00:00:00") {
              startingOrders[i].ReportTime = null;
            } else {
              startingOrders[i].ReportTime = {
                days: getDaysFromDate(reportTime),
                hours: reportTime.getUTCHours(),
                minutes: reportTime.getUTCMinutes(),
              };
            }

            startingOrders[i].TimeOrdered = {
              days: getDaysFromDate(timeOrdered),
              hours: timeOrdered.getUTCHours(),
              minutes: timeOrdered.getUTCMinutes(),
            };

            startingOrders[i].CurrentVirtualTime = {
              days: getDaysFromDate(currentVirtualTime),
              hours: currentVirtualTime.getUTCHours(),
              minutes: currentVirtualTime.getUTCMinutes(),
            };
          }
          startCaseData.currentOrders = startingOrders;
        }

        const ialist = simulationData.ialist ? [...simulationData.ialist] : [];

        // Fresh start: seed location/time the way App does when ialist is empty.
        // (When resuming we overwrite these from /incompletestart.webapi.)
        startCaseData.currentLocation = simulationData.CaseLocationId;
        startCaseData.currentVirtualTime = simulationData.InitialStartTime;

        // Newest attempt first — mirrors App's sort before the popup.
        ialist.sort(function (a, b) {
          return (
            new Date(b.TimeAttempted + "Z") - new Date(a.TimeAttempted + "Z")
          );
        });

        return {
          simulationData: simulationData,
          startCaseData: startCaseData,
          caseguid: simulationData.caseguid,
          ialist: ialist,
        };
      })
      .catch(function (error) {
        logError(error, "multiCcs.startCase");
        throw error;
      });
  }

  // ---------------------------------------------------------------------------
  // INCOMPLETE START  (forked from App.confirmIncompleteCaseStart)
  //
  // Resumes a partially-completed attempt. Must be called AFTER startCase for
  // the same case: the caseguid it takes is the one startCase just returned,
  // and CurrentStateId identifies which saved attempt to restore.
  //
  // Resolves with { simulationData, startCaseData } ready for <Simulation>.
  //
  // NOTE: App reads the body via a streaming reader. Keeping that shape here
  // rather than response.text() — same bytes either way, but this is the path
  // that's known to work against this handler.
  // ---------------------------------------------------------------------------

  function incompleteStart(input, signal) {
    const simulationData = Object.assign({}, input.simulationData);

    return fetch(route + "/incompletestart.webapi", {
      method: "POST",
      headers: {
        Token: token(),
        "Content-Type": "text/plain",
      },
      body: JSON.stringify({
        CustomerId: customerId(),
        caseguid: input.caseguid,
        CurrentStateId: input.currentStateId,
      }),
      signal: signal,
    })
      .then(function (response) {
        if (response.status === 401) {
          if (onAuthError) onAuthError();
          throw AuthError();
        }

        const decoder = new TextDecoder("utf-8");
        const reader = response.body.getReader();
        let result = "";

        return reader.read().then(function processText(chunk) {
          if (chunk.done) {
            return JSON.parse(result);
          }
          result += decoder.decode(chunk.value);
          return reader.read().then(processText);
        });
      })
      .then(function (data) {
        submitLogs();

        // Current simulated location
        simulationData.CaseLocationId = data.CurrentLocation;
        const locations = [
          "Emergency Department",
          "Office",
          "Intensive Care Unit",
          "Home",
          "Inpatient Unit",
        ];
        simulationData.CaseLocationDesc =
          locations[simulationData.CaseLocationId - 1];

        // Current simulated time
        const date = new Date(data.CurrentVirtualTime + "Z");
        const elapsedSimulatedCaseTime = {
          days: getDaysFromDate(date),
          hours: date.getUTCHours(),
          minutes: date.getUTCMinutes(),
        };

        const actionLogData = getActionLogData(JSON.parse(data.ActionLog));
        const patientUpdates = actionLogData.patientUpdates;
        const currentActualTime = actionLogData.currentActualTime;
        const physicalResults = actionLogData.physicalResults;
        const vitalsResults = actionLogData.vitalsResults;

        const currentOrders = formatCurrentOrders(
          JSON.parse(data.CurrentOrders),
        );
        const completedOrders = formatCompletedOrders(
          JSON.parse(data.CompletedOrders),
        );

        const startCaseData = {
          completedOrders: completedOrders,
          currentLocation: data.CurrentLocation,
          currentOrders: currentOrders,
          currentPatientUpdate: data.currentPatientUpdate,
          currentPhysicalExamId: data.currentPhysicalExamId,
          currentState: data.CurrentState,
          currentVirtualTime: simulationData.InitialStartTime,
          currentVitalsId: data.CurrentVitalsId,
          timeToPatientUpdate: data.timeToPatientUpdate,
          popupQueue: [],
          simulationOptionSelected: 1,
          centerContent: "reviewOrders",
          elapsedSimulatedCaseTime: elapsedSimulatedCaseTime,
          physicalResults: physicalResults,
          currentActualTime:
            currentActualTime === null ? 0 : currentActualTime.getMinutes(),
          caseEnds: data.CaseEnded,
          patientUpdates: patientUpdates,
          vitalsResults: vitalsResults,
        };

        return {
          simulationData: simulationData,
          startCaseData: startCaseData,
        };
      })
      .catch(function (error) {
        logError(error, "multiCcs.incompleteStart");
        throw error;
      });
  }

  // ---------------------------------------------------------------------------
  // GRADING  (forked from App.fetchGrading)
  //
  // Called once when a case ends. Returns the grading payload — MultiCcsApp
  // records caseguid + TotalGrade and routes to the break screen WITHOUT
  // showing the grade. The grade itself is re-fetched later, at review time,
  // via retrieveMultiCaseGrade(caseguid).
  //
  // Unlike App's version this does NOT refetch the case list or navigate.
  // ---------------------------------------------------------------------------

  function gradeCase(input, signal) {
    return post(
      "/grading.webapi",
      {
        customerId: customerId(),
        caseguid: input.caseguid,
        caseId: input.caseId,
      },
      signal,
    )
      .then(function (text) {
        submitLogs();
        return JSON.parse(text);
      })
      .catch(function (error) {
        logError(error, "multiCcs.gradeCase");
        throw error;
      });
  }

  // Gamification — fires after each case is graded, same contract as App's
  // triggerGamificationEvent. Best-effort: never rejects, so a gamification
  // outage can't strand a user mid-session.
  function triggerGamificationEvent(caseIds, scorePct, signal) {
    return fetch(route + "/triggergamificationevent.webapi", {
      method: "POST",
      headers: {
        Token: token(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ case_ids: caseIds, score_pct: scorePct }),
      signal: signal,
    })
      .then(function (response) {
        if (response.status === 401) {
          if (onAuthError) onAuthError();
          return null;
        }
        if (!response.ok) return null;
        return response.json();
      })
      .catch(function (error) {
        logError(error, "multiCcs.triggerGamificationEvent");
        return null;
      });
  }

  // ---------------------------------------------------------------------------
  // MULTICASE SESSION ENDPOINTS  (new)
  //
  // The frontend owns the CaseProgress blob and writes it whole on every save.
  // The backend's only edit is filling in CurrentStateId for in-progress
  // caseguids (the frontend can't know it — it isn't returned by startcase).
  // ---------------------------------------------------------------------------

  // POST /createmulticase.webapi  { Title, CaseIds, Settings }
  //   -> { ID, CaseProgress }
  function createMultiCase(input, signal) {
    return post(
      "/createmulticase.webapi",
      {
        Title: input.title,
        CaseIds: input.caseIds,
        Settings: input.settings,
      },
      signal,
    )
      .then(function (text) {
        return normalizeSession(parseJson(text));
      })
      .catch(function (error) {
        logError(error, "multiCcs.createMultiCase");
        throw error;
      });
  }

  // GET /listmulticases.webapi -> [{ ID, Title, CasesList, CaseProgress, Completed }]
  function listMultiCases(signal) {
    return get("/listmulticases.webapi", signal)
      .then(function (text) {
        const data = parseJson(text);
        const rows = Array.isArray(data)
          ? data
          : (data && (data.Sessions || data.MultiCases)) || [];
        return rows.map(normalizeSession);
      })
      .catch(function (error) {
        logError(error, "multiCcs.listMultiCases");
        throw error;
      });
  }

  // POST /getmulticase.webapi  { MultiCaseId }
  // Backend fills the current CurrentStateId for any in-progress caseguid.
  function getMultiCase(multiCaseId, signal) {
    return post("/getmulticase.webapi", { MultiCaseId: multiCaseId }, signal)
      .then(function (text) {
        return normalizeSession(parseJson(text));
      })
      .catch(function (error) {
        logError(error, "multiCcs.getMultiCase");
        throw error;
      });
  }

  // POST /savemulticaseprogress.webapi  { MultiCaseId, CaseProgress, Completed }
  // Whole-blob write. Returns the stored session (with CurrentStateId filled).
  function saveMultiCaseProgress(input, signal) {
    return post(
      "/savemulticaseprogress.webapi",
      {
        MultiCaseId: input.multiCaseId,
        CaseProgress: JSON.stringify(input.caseProgress),
        Completed: !!input.completed,
      },
      signal,
    )
      .then(function (text) {
        const data = parseJson(text);
        // Some handlers reply with a bare status string; treat that as "saved"
        // and let the caller keep the blob it already has.
        if (!data || typeof data === "string") return null;
        return normalizeSession(data);
      })
      .catch(function (error) {
        logError(error, "multiCcs.saveMultiCaseProgress");
        throw error;
      });
  }

  // POST /updatemulticasetitle.webapi  { MultiCaseId, Title }
  function updateMultiCaseTitle(multiCaseId, title, signal) {
    return post(
      "/updatemulticasetitle.webapi",
      { MultiCaseId: multiCaseId, Title: title },
      signal,
    )
      .then(function (text) {
        return parseJson(text);
      })
      .catch(function (error) {
        logError(error, "multiCcs.updateMultiCaseTitle");
        throw error;
      });
  }

  // POST /retreivemulticasegrade.webapi  { caseguid }
  //   -> same envelope as /retreivecasegrade.webapi
  // (Spelling matches the existing endpoint, which is also "retreive".)
  function retrieveMultiCaseGrade(caseguid, signal) {
    return post(
      "/retreivemulticasegrade.webapi",
      { caseguid: caseguid },
      signal,
    )
      .then(function (text) {
        submitLogs();
        return normalizeGrade(JSON.parse(text));
      })
      .catch(function (error) {
        logError(error, "multiCcs.retrieveMultiCaseGrade");
        throw error;
      });
  }

  // Review Later / High Yield — kept in the grade view, so MultiCCS needs them.
  function saveReviewLater(caseId, marked, signal) {
    return post(
      "/reviewlater.webapi",
      { customerId: customerId(), caseId: caseId, marked: marked },
      signal,
    ).catch(function (error) {
      logError(error, "multiCcs.saveReviewLater");
      throw error;
    });
  }

  function saveHighYield(caseId, markHighYield, signal) {
    return post(
      "/highyield.webapi",
      {
        customerid: customerId(),
        caseid: caseId,
        markhighyield: markHighYield,
        unmarkhighyield: !markHighYield,
      },
      signal,
    ).catch(function (error) {
      logError(error, "multiCcs.saveHighYield");
      throw error;
    });
  }

  // Reading options — shared with the main grading page, so a user's choices
  // carry across both.
  function getReadingOptions(signal) {
    return post(
      "/getsavedreadingoptions.webapi",
      { customerId: customerId() },
      signal,
    )
      .then(parseJson)
      .catch(function (error) {
        logError(error, "multiCcs.getReadingOptions");
        return null;
      });
  }

  function saveReadingOptions(readingOptions, signal) {
    return post(
      "/savereadingoptions.webapi",
      Object.assign({ customerId: customerId() }, readingOptions),
      signal,
    )
      .then(parseJson)
      .catch(function (error) {
        logError(error, "multiCcs.saveReadingOptions");
        return null;
      });
  }

  // POST /savemulticcssettings.webapi  { Settings }
  // Remembers the user's settings on the customer record so the next block —
  // on any device — starts from them. Best-effort: a failure here shouldn't
  // interrupt a block, since the block carries its own copy regardless.
  function saveMultiCcsSettings(settings, signal) {
    return post(
      "/savemulticcssettings.webapi",
      { Settings: normalizeSettings(settings) },
      signal,
    )
      .then(parseJson)
      .catch(function (error) {
        logError(error, "multiCcs.saveMultiCcsSettings");
        return null;
      });
  }

  function submitFeedback(input, signal) {
    const body = { customerId: customerId(), feedback: input.feedback };
    if (input.caseId !== null && input.caseId !== undefined) {
      body.caseId = input.caseId;
    }
    if (input.caseDiagnosis !== null && input.caseDiagnosis !== undefined) {
      body.caseDiagnosis = input.caseDiagnosis;
    }
    return post("/feedback.webapi", body, signal).catch(function (error) {
      logError(error, "multiCcs.submitFeedback");
      throw error;
    });
  }

  return {
    // case data
    listCases: listCases,
    // running a case
    startCase: startCase,
    incompleteStart: incompleteStart,
    gradeCase: gradeCase,
    triggerGamificationEvent: triggerGamificationEvent,
    // sessions
    createMultiCase: createMultiCase,
    listMultiCases: listMultiCases,
    getMultiCase: getMultiCase,
    saveMultiCaseProgress: saveMultiCaseProgress,
    updateMultiCaseTitle: updateMultiCaseTitle,
    // review
    retrieveMultiCaseGrade: retrieveMultiCaseGrade,
    saveReviewLater: saveReviewLater,
    saveHighYield: saveHighYield,
    getReadingOptions: getReadingOptions,
    saveReadingOptions: saveReadingOptions,
    saveMultiCcsSettings: saveMultiCcsSettings,
    submitFeedback: submitFeedback,
  };
}

// -----------------------------------------------------------------------------
// CaseProgress helpers
//
// CaseProgress is stored as an NVARCHAR(MAX) JSON blob. Everything that reads or
// writes it goes through here so the shape stays in one place.
// -----------------------------------------------------------------------------

export const DEFAULT_SETTINGS = {
  EnforceCaseTimeLimit: true,
  CustomTimeLimit: "none",
  NetworkLag: "none",
  EnforceBreak: false,
  BreakMinutes: 5,
  HideCaseTitle: false,
  HideCaseCategory: false,
};

// The user's last-used settings, parsed out of CUSTOMER.MultiCcsSettings as it
// arrives on the login payload. Empty string means the customer has never saved
// any (the column is NULL), so fall back to the stock defaults.
//
// A new block starts from these; once created, the block owns its own copy in
// the blob, so editing an old block can't retroactively change a finished one.
export function parseRememberedSettings(raw) {
  if (!raw || typeof raw !== "string" || !raw.trim()) {
    return Object.assign({}, DEFAULT_SETTINGS);
  }
  try {
    return normalizeSettings(JSON.parse(raw));
  } catch (e) {
    // Corrupt column contents shouldn't break the builder.
    return Object.assign({}, DEFAULT_SETTINGS);
  }
}

export function normalizeSettings(raw) {
  const parsed = raw && typeof raw === "object" ? raw : {};
  return {
    EnforceCaseTimeLimit:
      parsed.EnforceCaseTimeLimit !== undefined
        ? !!parsed.EnforceCaseTimeLimit
        : DEFAULT_SETTINGS.EnforceCaseTimeLimit,
    CustomTimeLimit:
      parsed.CustomTimeLimit !== undefined && parsed.CustomTimeLimit !== null
        ? String(parsed.CustomTimeLimit)
        : DEFAULT_SETTINGS.CustomTimeLimit,
    NetworkLag:
      parsed.NetworkLag !== undefined && parsed.NetworkLag !== null
        ? String(parsed.NetworkLag)
        : DEFAULT_SETTINGS.NetworkLag,
    EnforceBreak:
      parsed.EnforceBreak !== undefined
        ? !!parsed.EnforceBreak
        : DEFAULT_SETTINGS.EnforceBreak,
    BreakMinutes:
      typeof parsed.BreakMinutes === "number" && parsed.BreakMinutes >= 0
        ? parsed.BreakMinutes
        : DEFAULT_SETTINGS.BreakMinutes,
    HideCaseTitle:
      parsed.HideCaseTitle !== undefined
        ? !!parsed.HideCaseTitle
        : DEFAULT_SETTINGS.HideCaseTitle,
    HideCaseCategory:
      parsed.HideCaseCategory !== undefined
        ? !!parsed.HideCaseCategory
        : DEFAULT_SETTINGS.HideCaseCategory,
  };
}

// Build the initial blob at creation time. Every case is seeded as pending so
// the break screen can render the full list before anything has been run.
export function buildCaseProgress(caseIds, title, settings) {
  const now = new Date().toISOString();
  return {
    Title: title,
    CreatedDate: now,
    ModifiedDate: now,
    Settings: normalizeSettings(settings),
    BreakRemainingSeconds: null,
    Cases: caseIds.map(function (caseId) {
      return {
        CaseId: caseId,
        Status: CASE_STATUS.PENDING,
        caseguid: null,
        CurrentStateId: null,
        GradeForCase: null,
        StartedAt: null,
        CompletedAt: null,
        ElapsedSeconds: null,
      };
    }),
  };
}

// Tolerant parse — the blob may arrive as a JSON string or an already-parsed
// object depending on how the handler serializes it. Falls back to rebuilding
// from CasesList if CaseProgress is missing or unreadable.
export function parseCaseProgress(raw, casesList) {
  let parsed = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) parsed = null;
    else {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        parsed = null;
      }
    }
  }

  if (!parsed || typeof parsed !== "object") {
    return buildCaseProgress(casesList || [], "", DEFAULT_SETTINGS);
  }

  const cases = Array.isArray(parsed.Cases) ? parsed.Cases : [];
  return {
    Title: parsed.Title || "",
    CreatedDate: parsed.CreatedDate || null,
    ModifiedDate: parsed.ModifiedDate || null,
    Settings: normalizeSettings(parsed.Settings),
    BreakRemainingSeconds:
      typeof parsed.BreakRemainingSeconds === "number"
        ? parsed.BreakRemainingSeconds
        : null,
    Cases: cases.map(function (entry) {
      return {
        CaseId: entry.CaseId,
        Status: entry.Status || CASE_STATUS.PENDING,
        caseguid: entry.caseguid || null,
        CurrentStateId:
          entry.CurrentStateId !== undefined ? entry.CurrentStateId : null,
        GradeForCase:
          typeof entry.GradeForCase === "number" ? entry.GradeForCase : null,
        StartedAt: entry.StartedAt || null,
        CompletedAt: entry.CompletedAt || null,
        ElapsedSeconds:
          typeof entry.ElapsedSeconds === "number"
            ? entry.ElapsedSeconds
            : null,
      };
    }),
  };
}

// A session row as the UI wants it: CasesList and CaseProgress already parsed.
export function normalizeSession(row) {
  if (!row) return null;

  let casesList = row.CasesList;
  if (typeof casesList === "string") {
    try {
      casesList = JSON.parse(casesList);
    } catch (e) {
      casesList = [];
    }
  }
  if (!Array.isArray(casesList)) casesList = [];

  const caseProgress = parseCaseProgress(row.CaseProgress, casesList);

  return {
    ID: row.ID,
    Title: row.Title || caseProgress.Title || "",
    CasesList: casesList,
    CaseProgress: caseProgress,
    Completed: !!row.Completed,
  };
}

// The grade envelope from /retreivemulticasegrade.webapi needs the same
// massaging NewGrading applies to /retreivecasegrade.webapi: drop uncategorized
// rows, sort by category, mark Correct, and strip "State Changed to:" noise from
// the action log.
export function normalizeGrade(data) {
  if (!data || !data.GradeFeedback) return data;

  const feedback = data.GradeFeedback;

  if (Array.isArray(feedback.GradeOut)) {
    feedback.GradeOut.sort(function (a, b) {
      if (a.GradeCategory < b.GradeCategory) return -1;
      if (a.GradeCategory > b.GradeCategory) return 1;
      return 0;
    });
    feedback.GradeOut = feedback.GradeOut.filter(function (grade) {
      return grade.GradeCategory !== "";
    });
    for (let i = 0; i < feedback.GradeOut.length; i++) {
      feedback.GradeOut[i].Correct =
        typeof feedback.GradeOut[i].GradeColor === "string" &&
        feedback.GradeOut[i].GradeColor.includes("Correct - Green");
    }
  } else {
    feedback.GradeOut = [];
  }

  if (Array.isArray(feedback.ActionLogList)) {
    for (let i = 0; i < feedback.ActionLogList.length; ++i) {
      if (
        feedback.ActionLogList[i].EventName &&
        feedback.ActionLogList[i].EventName.includes("State Changed to:")
      ) {
        feedback.ActionLogList.splice(i, 1);
        i--;
      }
    }
  } else {
    feedback.ActionLogList = [];
  }

  return data;
}

// --- session-level derivations ----------------------------------------------

export function completedCount(caseProgress) {
  if (!caseProgress || !caseProgress.Cases) return 0;
  return caseProgress.Cases.filter(function (entry) {
    return entry.Status === CASE_STATUS.COMPLETED;
  }).length;
}

// The next case to run: the first entry that isn't finished. Order is fixed at
// creation, like the real exam. Returns null once the block is done.
export function nextCaseEntry(caseProgress) {
  if (!caseProgress || !caseProgress.Cases) return null;
  for (let i = 0; i < caseProgress.Cases.length; i++) {
    if (caseProgress.Cases[i].Status !== CASE_STATUS.COMPLETED) {
      return caseProgress.Cases[i];
    }
  }
  return null;
}

export function isSessionComplete(caseProgress) {
  return nextCaseEntry(caseProgress) === null;
}

// Auto-title: "4 Cases — Jul 15, 2026". Users can rename later.
export function defaultSessionTitle(caseCount, date) {
  const when = date || new Date();
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const stamp =
    MONTHS[when.getMonth()] + " " + when.getDate() + ", " + when.getFullYear();
  return (
    caseCount + (caseCount === 1 ? " Case" : " Cases") + " \u2014 " + stamp
  );
}

// Total real-time minutes for a case, honouring the session's CustomTimeLimit.
//
// This is the whole allowance the way the case list shows it — a "10 Minute
// Case" is 10 minutes total, of which Simulation reserves the last 2 for
// case-end orders (so 8 minutes of case work + 2). The subtraction happens
// inside Simulation.componentDidMount; don't apply it here too.
export function caseTimeLimitMinutes(caseRow, settings) {
  const config = normalizeSettings(settings);
  if (!config.EnforceCaseTimeLimit) return null;

  const defaultTime = TIME_MODE_MINUTES[caseRow.TIMEMODEID] || 10;
  if (config.CustomTimeLimit === "none" || config.CustomTimeLimit === "") {
    return defaultTime;
  }
  if (config.CustomTimeLimit.indexOf("pct_") === 0) {
    const pct = parseInt(config.CustomTimeLimit.split("_")[1], 10);
    if (isNaN(pct)) return defaultTime;
    return Math.round(defaultTime * (1 + pct / 100));
  }
  const explicit = parseFloat(config.CustomTimeLimit);
  return isNaN(explicit) ? defaultTime : explicit;
}

// Elapsed wall-clock seconds for a case, from StartedAt -> CompletedAt.
export function elapsedSeconds(startedAt, completedAt) {
  if (!startedAt || !completedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (isNaN(start) || isNaN(end) || end < start) return null;
  return Math.round((end - start) / 1000);
}

export function formatElapsed(seconds) {
  if (typeof seconds !== "number" || seconds < 0) return "\u2014";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins + "m " + (secs < 10 ? "0" + secs : secs) + "s";
}

// --- case selection ----------------------------------------------------------

// Fisher-Yates. Random blocks get a random order too.
export function shuffle(list) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i];
    out[i] = out[j];
    out[j] = tmp;
  }
  return out;
}

// A case counts as completed only if it also has a real completion date —
// the same guard the main case list uses ("0001-01-01T00:00:00" means never).
export function isCaseCompleted(caseRow) {
  return (
    !!caseRow.COMPLETED && caseRow.LastCompletionDate !== "0001-01-01T00:00:00"
  );
}

// Random selection. Filters combine; hidden cases are deliberately ignored.
// Demo cases are excluded — demo accounts can't reach MultiCCS at all.
//
//   filters = {
//     includeCompleted: bool,
//     includeIncomplete: bool,
//     categories: [CASECATEGORYDESC],   // empty = all
//   }
//
// Returns { caseIds, error }. Asking for more than the pool holds is an error,
// per spec — we don't silently hand back a shorter block.
export function pickRandomCases(cases, count, filters) {
  const config = filters || {};
  const categories = config.categories || [];

  const pool = cases.filter(function (caseRow) {
    if (caseRow.DEMO === "Yes") return false;

    const completed = isCaseCompleted(caseRow);
    if (completed && !config.includeCompleted) return false;
    if (!completed && !config.includeIncomplete) return false;

    if (
      categories.length > 0 &&
      categories.indexOf(caseRow.CASECATEGORYDESC) === -1
    ) {
      return false;
    }
    return true;
  });

  if (count < MIN_BLOCK_SIZE || count > MAX_BLOCK_SIZE) {
    return {
      caseIds: [],
      error:
        "Pick between " +
        MIN_BLOCK_SIZE +
        " and " +
        MAX_BLOCK_SIZE +
        " cases for a block.",
    };
  }

  if (pool.length < count) {
    return {
      caseIds: [],
      error:
        "Only " +
        pool.length +
        (pool.length === 1 ? " case matches" : " cases match") +
        " those filters, but you asked for " +
        count +
        ". Widen the filters or lower the count.",
    };
  }

  return {
    caseIds: shuffle(pool)
      .slice(0, count)
      .map(function (caseRow) {
        return caseRow.ID;
      }),
    error: null,
  };
}